import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MessageCircle, Clock, MapPin, Navigation, CheckCircle2, Send } from 'lucide-react';
import { CLINIC, IMAGES } from '../mock';
import { PageHero, SectionTitle, Reveal } from '../components/Shared';
import { LOCATIONS } from './OurLocations';
import { FirstVisit } from '../components/FirstVisit';
import { OnlineConsult } from '../components/OnlineConsult';
import { useLang } from '../context/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', phone: '', email: '', location: 'Ghaziabad', message: '' };
const INPUT = 'w-full px-4 py-3 rounded-xl border border-teal-100 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition';

const METHODS = [
  { icon: Phone, label: 'Call us', hi: 'कॉल करें', value: CLINIC.phone, href: `tel:${CLINIC.phoneRaw}`, id: 'phone' },
  { icon: MessageCircle, label: 'WhatsApp', hi: 'व्हाट्सऐप', value: 'Chat with our team', hiValue: 'हमारी टीम से बात करें', href: CLINIC.whatsapp, id: 'whatsapp', external: true },
  { icon: Mail, label: 'Email', hi: 'ईमेल', value: CLINIC.email, href: `mailto:${CLINIC.email}`, id: 'email' },
];

function ContactMethods() {
  const { tr } = useLang();
  return (
    <div className="space-y-4">
      {METHODS.map((m) => (
        <a key={m.id} data-testid={`contact-method-${m.id}`} href={m.href} {...(m.external ? { target: '_blank', rel: 'noreferrer' } : {})}
          className="card-lift flex items-center gap-4 bg-white border border-teal-50 rounded-2xl p-5">
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700 shrink-0"><m.icon size={22} /></span>
          <div className="min-w-0"><div className="text-sm text-slate-500">{tr(m.label, m.hi)}</div><div className="font-semibold text-teal-900 break-all">{tr(m.value, m.hiValue)}</div></div>
        </a>
      ))}
      <div className="bg-white border border-teal-50 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-3"><Clock size={19} className="text-teal-600" /><span className="font-semibold text-teal-900">{tr('Main clinic hours', 'मुख्य क्लिनिक का समय')}</span></div>
        <ul className="text-sm divide-y divide-teal-50">
          {CLINIC.hours.map((h) => (
            <li key={h.d} className="flex justify-between py-1.5"><span className="text-slate-600">{tr(h.d, h.hi)}</span><span style={{ color: h.t === 'Closed' ? 'var(--coral)' : 'var(--teal-700)' }}>{tr(h.t, { 'Closed': 'बंद', 'By Appointment': 'अपॉइंटमेंट से' }[h.t])}</span></li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const { tr } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, { ...form, email: form.email.trim() || null });
      setForm(EMPTY);
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') return (
    <div data-testid="contact-success" className="text-center py-14">
      <span className="grid place-items-center w-16 h-16 rounded-full bg-teal-50 text-teal-600 mx-auto mb-4"><CheckCircle2 size={34} /></span>
      <h3 className="font-display text-2xl text-teal-900" style={{ fontWeight: 600 }}>{tr('Message sent!', 'संदेश भेज दिया गया!')}</h3>
      <p className="text-slate-600 mt-2">{tr('Thank you. Our team will call you back shortly.', 'धन्यवाद। हमारी टीम जल्द ही आपको कॉल करेगी।')}</p>
      <button data-testid="contact-send-another" onClick={() => setStatus('idle')} className="text-teal-700 font-semibold text-sm mt-5">{tr('Send another message', 'एक और संदेश भेजें')}</button>
    </div>
  );

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{tr('Full Name', 'पूरा नाम')}</label><input data-testid="contact-name-input" required value={form.name} onChange={set('name')} className={INPUT} placeholder={tr('Your name', 'आपका नाम')} /></div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{tr('Phone Number', 'फ़ोन नंबर')}</label><input data-testid="contact-phone-input" required value={form.phone} onChange={set('phone')} className={INPUT} placeholder={tr('Mobile number', 'मोबाइल नंबर')} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{tr('Email', 'ईमेल')} <span className="text-slate-400">{tr('(optional)', '(वैकल्पिक)')}</span></label><input data-testid="contact-email-input" type="email" value={form.email} onChange={set('email')} className={INPUT} placeholder={tr('For a confirmation email', 'पुष्टि ईमेल के लिए')} /></div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{tr('Preferred Location', 'पसंदीदा क्लिनिक')}</label>
          <select data-testid="contact-location-select" value={form.location} onChange={set('location')} className={`${INPUT} bg-white`}>
            {LOCATIONS.map((l) => <option key={l.city} value={l.city}>{tr(l.city, l.hiCity)}</option>)}
          </select>
        </div>
      </div>
      <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{tr('Message', 'संदेश')}</label><textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set('message')} className={`${INPUT} resize-none`} placeholder={tr('How can we help you?', 'हम आपकी क्या मदद कर सकते हैं?')} /></div>
      {status === 'error' && <p data-testid="contact-error" className="text-sm text-center" style={{ color: 'var(--coral-dark)' }}>{tr('Something went wrong. Please call or WhatsApp us instead.', 'कुछ गड़बड़ हो गई। कृपया कॉल या व्हाट्सऐप करें।')}</p>}
      <button data-testid="contact-submit-button" type="submit" disabled={status === 'loading'} className="btn-coral w-full py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70">
        <Send size={17} /> {status === 'loading' ? tr('Sending...', 'भेज रहे हैं...') : tr('Send Message', 'संदेश भेजें')}
      </button>
    </form>
  );
}

function ClinicMaps() {
  const { tr } = useLang();
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {LOCATIONS.map((l, i) => (
        <Reveal key={l.city} delay={(i % 2) * 80}>
          <div data-testid={`contact-map-${l.city.toLowerCase()}`} className="bg-white rounded-3xl border border-teal-50 overflow-hidden shadow-sm h-full">
            <iframe title={l.name} src={l.map} className="w-full h-60 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            <div className="p-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-teal-600">{tr(l.type, l.hiType)}</span>
              <h3 className="font-display text-xl text-teal-900 mt-1" style={{ fontWeight: 600 }}>{l.name}</h3>
              <p className="flex gap-2 text-sm text-slate-600 mt-3"><MapPin size={17} className="text-teal-600 shrink-0" />{l.address}</p>
              <p className="flex gap-2 text-sm text-slate-600 mt-2"><Clock size={17} className="text-teal-600 shrink-0" />{tr(l.hours, l.hiHours || 'अपॉइंटमेंट से — कृपया कॉल करके पुष्टि करें')}</p>
              <a href={`https://maps.google.com/maps?q=${encodeURIComponent(l.address)}`} target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-white mt-5"><Navigation size={15} /> {tr('Directions', 'रास्ता')}</a>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export default function Contact() {
  const { tr } = useLang();
  return (
    <div>
      <PageHero eyebrow={tr('Contact Us', 'संपर्क करें')} title={tr("We're here to help", 'हम आपकी मदद के लिए यहाँ हैं')} subtitle={tr('Call, WhatsApp, email or send us a message. Our team will get back to you to arrange your consultation.', 'कॉल, व्हाट्सऐप, ईमेल करें या संदेश भेजें। हमारी टीम आपसे संपर्क करके परामर्श तय करेगी।')} image={IMAGES.examRoom} />
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-[minmax(0,380px)_1fr] gap-10 items-start">
          <ContactMethods />
          <div className="bg-white rounded-3xl border border-teal-50 shadow-sm p-7 md:p-9">
            <SectionTitle eyebrow={tr('Send a message', 'संदेश भेजें')} title={tr('Tell us how we can help', 'बताइए हम कैसे मदद करें')} />
            <ContactForm />
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="container-x">
          <SectionTitle eyebrow={tr('Planning a visit?', 'मिलने आ रहे हैं?')} title={tr('What to expect at your first appointment', 'पहली मुलाक़ात में क्या होगा')} />
          <FirstVisit />
        </div>
      </section>
      <section data-testid="contact-online-consult-section" className="pb-20">
        <div className="container-x"><OnlineConsult /></div>
      </section>
      <section className="mint-section py-20">
        <div className="container-x">
          <SectionTitle center eyebrow={tr('Find Us', 'हमें खोजें')} title={tr('All our clinic locations', 'हमारे सभी क्लिनिक')} />
          <ClinicMaps />
        </div>
      </section>
    </div>
  );
}
